import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { ArticleCard } from "@/components/ArticleCard";
import { useI18n } from "@/i18n";
import { SponsoredCard } from "@/components/SponsoredCard";
import { CategoryIcon } from "@/components/CategoryTag";
import { useAds, sponsoredAt } from "@/lib/ads";

export function FeedGrid({ baseParams = {}, title, description, icon }) {
  const { t } = useI18n();
  const { data: ads } = useAds();
  const [page, setPage] = useState(0);
  const [items, setItems] = useState([]);
  const limit = 12;

  const paramStr = new URLSearchParams({ ...baseParams, skip: page * limit, limit }).toString();

  const { data, isLoading } = useQuery({
    queryKey: ["feed", paramStr],
    queryFn: async () => {
      const res = (await api.get(`/articles?${paramStr}`)).data;
      setItems((prev) => (page === 0 ? res.items : [...prev, ...res.items]));
      return res;
    },
  });

  return (
    <div className="pt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-4">
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight inline-flex items-center gap-3">{icon && <span className="inline-flex w-11 h-11 items-center justify-center rounded-xl bg-violet-600 text-white"><CategoryIcon slug={icon} size={22} /></span>}{title}</h1>
        {description && <p className="mt-2 text-slate-600 max-w-2xl">{description}</p>}
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        {items.length === 0 && !isLoading && (
          <div className="bg-white rounded-xl border border-dashed border-slate-300 p-10 text-center text-sm text-muted-foreground">{t("common.noStories")}</div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((a, i) => (
            <React.Fragment key={a.id}>
              {sponsoredAt(i, ads) !== undefined && <SponsoredCard card={sponsoredAt(i, ads)} />}
              <ArticleCard article={a} index={i} />
            </React.Fragment>
          ))}
        </div>
        {data?.has_more && (
          <div className="mt-8 text-center">
            <button data-testid="load-more-btn" onClick={() => setPage((p) => p + 1)} className="px-6 py-2.5 bg-white border border-slate-300 rounded-full text-sm font-medium hover:border-violet-300 hover:text-violet-700 transition-colors">{t("common.loadMore")}</button>
          </div>
        )}
      </div>
    </div>
  );
}
